import React from "react";
import { Trans } from "react-i18next";
import styled from "styled-components/macro";
import { faPause, faPlay } from "@duocards/pro-solid-svg-icons";
import { useGetState, useSetState } from "components/ReduxProvider";
import { DifficultyEnum } from "sharedJs__generated/constants";
import { useViewerDecks, useViewerQuery } from "queries/viewerQuery";
import { canTts } from "queries/tts/ttsTools";
import { ControlButton } from "./ControlButton";
import { snackbar } from "tools/events";
const Wrap = styled.div`
  margin: 10px 10px 20px 10px;
  padding: 10px;
  text-align: center;
  border-width: 0;
  position: relative;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
`;

type Props = {
  onPlay: () => void;
  onStop: () => void;
  playing: boolean;
};

export const useListenVocabTranslationSupported = () => {
  const { deck } = useViewerDecks();
  return deck?.front && deck?.back ? canTts(deck.front) && canTts(deck.back) : false;
};

export const useListenVocabSettings = () => {
  const listenVocabSettings = useGetState("listenVocabSettings");
  const { getFlag } = useViewerQuery();
  const setListenVocabSettings = useSetState("listenVocabSettings");
  const translationSupported = useListenVocabTranslationSupported();

  const isBeginnerOrEasy = [DifficultyEnum.beginner, DifficultyEnum.easy].includes(
    getFlag("myDifficulty")?.value as DifficultyEnum
  );

  const withTranslation = translationSupported ? listenVocabSettings?.withTranslation ?? isBeginnerOrEasy : false;
  const settings = { withTranslation };

  return [settings, setListenVocabSettings] as const;
};

export const ListenVocabControls = ({ onPlay, onStop, playing }: Props) => {
  const volume = useGetState("volume");

  React.useEffect(() => {
    if (volume === 0 && playing) {
      onStop();
      snackbar(<Trans>Your device is muted. Enable sound to continue.</Trans>, { severity: "warning" });
    }
  }, [volume, playing, onStop]);

  return (
    <Wrap>
      <ButtonContainer>
        <ControlButton icon={playing ? faPause : faPlay} onClick={playing ? onStop : onPlay} />
      </ButtonContainer>
    </Wrap>
  );
};
